import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useClient } from "../context/ClientContext";
import { PageHeader, Section, Card, CardHeader, Stat, Pill, LoadingBar } from "../components/Primitives";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend
} from "recharts";
import { ArrowUpRight } from "@phosphor-icons/react";

const RCS_BAND_COLOR = {
  Recommended: "text-emerald-700",
  Conditional: "text-amber-700",
  Hold: "text-rose-700",
};

export default function ExecutiveDashboard() {
  const { activeClient, activeClientId } = useClient();
  const [rcs, setRcs] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [trends, setTrends] = useState(null);

  useEffect(() => {
    if (!activeClientId) return;
    setRcs(null); setKpis(null); setTrends(null);
    const params = { params: { client_id: activeClientId } };
    Promise.all([
      api.get("/executive/rcs", params),
      api.get("/executive/kpis", params),
      api.get("/executive/trends", params),
    ]).then(([a, b, c]) => { setRcs(a.data); setKpis(b.data); setTrends(c.data); });
  }, [activeClientId]);

  if (!rcs || !kpis || !trends) return <LoadingBar label="Synthesising release confidence" />;

  return (
    <div data-testid="executive-dashboard">
      <PageHeader
        overline={`${activeClient?.name || ""} · ${activeClient?.platform || ""} · ${rcs.release_label}`}
        title="Executive Assurance"
        description="Cross-workflow release confidence, defect trajectory, and AI-augmented coverage — at a glance."
        right={<Pill tone="info" testid="release-band-pill">{rcs.band} Release</Pill>}
        testid="executive-header"
      />

      <Section>
        <div className="grid grid-cols-12 gap-6">
          {/* RCS hero */}
          <Card className="col-span-12 lg:col-span-5 p-8" testid="rcs-hero-card">
            <div className="overline text-zinc-500 mb-4">Release Confidence Score</div>
            <div className="flex items-baseline gap-4">
              <div className={`metric-value text-[120px] leading-none ${RCS_BAND_COLOR[rcs.band] || "text-zinc-900"}`}>
                {rcs.score}
              </div>
              <div className="text-zinc-400 font-mono text-2xl">/100</div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Pill tone={rcs.band === "Recommended" ? "success" : rcs.band === "Hold" ? "danger" : "warn"} testid="rcs-band">
                {rcs.band}
              </Pill>
              <div className="text-xs font-mono text-zinc-500">
                Δ <span className={rcs.delta_vs_last_release >= 0 ? "text-emerald-700" : "text-rose-700"}>
                  {rcs.delta_vs_last_release >= 0 ? "+" : ""}{rcs.delta_vs_last_release}
                </span> vs last release
              </div>
            </div>
            <p className="mt-6 text-sm text-zinc-600 leading-relaxed border-l-2 border-vlink-500 pl-4">
              {rcs.recommendation}
            </p>

            <div className="mt-8 space-y-3">
              {rcs.contributors.map((c) => (
                <div key={c.label} className="flex items-center gap-3 text-xs">
                  <div className="w-32 text-zinc-600">{c.label}</div>
                  <div className="flex-1 h-1 bg-zinc-100 relative">
                    <div className="absolute inset-y-0 left-0 bg-zinc-900" style={{ width: `${c.value}%` }} />
                  </div>
                  <div className="w-12 text-right font-mono text-zinc-700">{c.value}</div>
                  <div className="w-12 text-right font-mono text-zinc-400">×{c.weight.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* KPI grid */}
          <div className="col-span-12 lg:col-span-7 grid grid-cols-2 gap-6">
            {kpis.map((k) => (
              <Card key={k.key} className="p-6" testid={`kpi-${k.key}`}>
                <Stat label={k.label} value={k.value} suffix={k.unit || ""} delta={k.delta} />
                <div className="mt-4 flex items-center gap-2 text-xs font-mono text-zinc-500">
                  <span>Target {k.target}{k.unit}</span>
                  <div className="flex-1 h-px bg-zinc-200" />
                  <span className={k.value >= k.target ? "text-emerald-700" : "text-amber-700"}>
                    {k.value >= k.target ? "On track" : `${k.target - k.value}${k.unit} gap`}
                  </span>
                </div>
              </Card>
            ))}
          </div>

          <Card className="col-span-12 lg:col-span-7" testid="rcs-trend-card">
            <CardHeader overline="Last 9 quarters" title="Release Confidence Trajectory" />
            <div className="px-4 pt-4 pb-6 h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends.rcs_trend}>
                  <CartesianGrid stroke="#F4F4F5" vertical={false} />
                  <XAxis dataKey="period" tick={{ fill: "#71717A", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#71717A", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#0F172A", color: "#fff", border: "none", fontSize: 12 }} />
                  <Line type="monotone" dataKey="rcs" stroke="#00AEEF" strokeWidth={2} dot={{ r: 3, fill: "#00AEEF" }} />
                  <Line type="monotone" dataKey="leakage" stroke="#E11D48" strokeWidth={1.2} strokeDasharray="3 3" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="col-span-12 lg:col-span-5" testid="defect-flow-card">
            <CardHeader overline="Last 8 weeks" title="Defect Flow" />
            <div className="px-4 pt-4 pb-6 h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends.defect_trend}>
                  <CartesianGrid stroke="#F4F4F5" vertical={false} />
                  <XAxis dataKey="week" tick={{ fill: "#71717A", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#71717A", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#0F172A", color: "#fff", border: "none", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="introduced" fill="#52525B" />
                  <Bar dataKey="resolved" fill="#00AEEF" />
                  <Bar dataKey="escaped" fill="#E11D48" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        <div className="mt-10 flex items-center gap-3 text-xs text-zinc-500">
          <ArrowUpRight size={14} />
          <span className="font-mono uppercase tracking-widest">Drill into Workflow Graph for handoff-level evidence</span>
        </div>
      </Section>
    </div>
  );
}
